import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import WebAppService from '../services/WebAppService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../store/Auth';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface PlansContextType {
  plans: any[];
  loading: boolean;
  subscription: any;
  isSubscribed: boolean;
  hasSubscriptionEnded: boolean;
  refetchSubscription: () => void;
  subscriptionLoading: boolean;
  isPilotOrg: boolean;
}

const PlansContext = createContext<PlansContextType | null>(null);

export const PlansProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, organization, isPilot } = useAuthStore();
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<any>({});
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [hasSubscriptionEnded, setHasSubscriptionEnded] =
    useState<boolean>(false);
  const [planExpiry, setPlanExpiry] = useState<string>('');
  const queryClient = useQueryClient();
  

  // Check if the organization is in pilot mode
  const isPilotOrg = isPilot;

  // Only fetch subscription if the user is authenticated and not in a pilot organization
  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery({
    queryKey: ['subscription', (user as any)?.id],
    queryFn: WebAppService.getCurrentSubscription,
    enabled: isAuthenticated && !isPilotOrg,
  });

  // Handle query errors

  useEffect(() => {
    if (subscriptionError) {
      console.error('Error fetching subscription:', subscriptionError);
    }
  }, [subscriptionError]);

  useEffect(() => {
    // If in pilot mode, consider everything as loaded and subscribed
    if (isPilotOrg) {
      setLoading(false);
      setIsSubscribed(true);
      setHasSubscriptionEnded(false);
      setSubscription({
        isPilot: true,
        plan: {
          name: '',
          features: [],
        },
        expiryDate: null,
      });
      setPlanExpiry('');
      return;
    }

    // Normal subscription logic for non-pilot organizations
    setLoading(subscriptionLoading);

    if ((subscriptionData as any)?.data !== null) {
      setSubscription((subscriptionData as any)?.data);
      setIsSubscribed(true);
      setPlanExpiry((subscriptionData as any)?.data?.expiryDate);
    } else {
      setIsSubscribed(false);
      setPlanExpiry('');
    }
  }, [subscriptionData, subscriptionLoading, isPilotOrg]);

  const userId = (user as any)?.id;

  const refetchSubscription = useCallback(() => {
    if (!isPilotOrg) {
      queryClient.invalidateQueries({ queryKey: ['subscription', userId] });
    }
  }, [isPilotOrg, queryClient, userId]);

  useEffect(() => {
    // Skip subscription checks for pilot organizations
    if (isPilotOrg) {
      setHasSubscriptionEnded(false);
      return;
    }

    if (isSubscribed && planExpiry !== '') {
      const currentDate = new Date();
      const expiryDate = new Date(planExpiry);
      if (currentDate > expiryDate) {
        setHasSubscriptionEnded(true);
      } else {
        setHasSubscriptionEnded(false);
      }
    }
  }, [isSubscribed, planExpiry, isPilotOrg]);

  const contextValue = useMemo(
    () => ({
      plans,
      loading,
      subscription,
      isSubscribed,
      hasSubscriptionEnded,
      refetchSubscription,
      subscriptionLoading,
      isPilotOrg,
    }),
    [
      plans,
      loading,
      subscription,
      isSubscribed,
      hasSubscriptionEnded,
      refetchSubscription,
      subscriptionLoading,
      isPilotOrg,
    ]
  );

  return (
    <PlansContext.Provider value={contextValue}>
      {children}
    </PlansContext.Provider>
  );
};

// Custom hook to use the PlansContext
export const usePlans = (): PlansContextType => {
  return useContext(PlansContext) as PlansContextType;
};
